<template>
    <div class="container my-2">
        <h1 class="mb-3">
            Zahlungsaufforderungen an {{ recipientLabel }}
        </h1>
        <div class="card mb-4">
            <div class="card-body">
                <p v-if="paymentRequestData.recipientName" class="mb-1">
                    <strong>Name:</strong> {{ paymentRequestData.recipientName }}
                </p>
                <p v-if="paymentRequestData.recipientMail" class="mb-1">
                    <strong>E-Mail:</strong> {{ paymentRequestData.recipientMail }}
                </p>
                <p v-if="paymentRequestData.iban" class="mb-1">
                    <strong>IBAN:</strong> {{ paymentRequestData.iban }}
                </p>
                <p v-if="paymentRequestData.paymentName" class="mb-0">
                    <strong>Zahlungsempfänger:</strong> {{ paymentRequestData.paymentName }}
                </p>
            </div>
        </div>

        <div v-if="isLoading" class="d-flex justify-content-center">
            <csp-loading-indicator/>
        </div>
        <div v-else>
            <div v-if="Object.keys(paymentRequestData.sentRequests).length" class="mb-4">
                <h2 class="mb-3">
                    Erstellte Zahlungsaufforderungen
                </h2>
                <div v-for="(sentRequest, index) in paymentRequestData.sentRequests"
                    :key="index"
                    class="card mb-3">
                    <div class="card-header mb-1">
                        <strong>Zahlungsaufforderung vom {{ sentRequest.date }}</strong>
                        <br>
                        <strong>Verwendungszweck:</strong> <br> {{ sentRequest.reference }}
                    </div>
                    <div class="card-body mb-2">
                        <div v-if="sentRequest.assets">
                            <ul>
                                <li v-for="(asset, aIndex) in sentRequest.assets"
                                    :key="aIndex" class="mb-1">
                                    {{ asset.label }}<br>
                                    <strong>Betrag:</strong> {{ formatCurrencyCents(asset.amount) }}
                                </li>
                            </ul>
                        </div>

                        <div v-if="sentRequest.payments">
                            <ul>
                                <li v-for="(payment, pIndex) in sentRequest.payments" :key="pIndex"
                                    class="mb-1">
                                    Zahlungseingang am {{ payment.date }} über
                                    <strong>{{ formatCurrencyCents(payment.amount) }}</strong> <br>
                                    <div v-if="payment.iban">
                                        von {{ payment.senderName }}, {{ payment.iban }},
                                        {{ payment.bic }}, {{ payment.bankName }}
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="paymentRequestData.possibleAssets.length || paymentRequestData.missingInformation.length">
                <h2 class="mb-3">
                    Zahlungsaufforderung erstellen
                </h2>
                <div v-if="paymentRequestData.missingInformation.length" class="alert alert-warning mb-3">
                    <div v-for="(warning, index) in paymentRequestData.missingInformation" :key="index">
                        {{ warning }}
                    </div>
                </div>
                <div v-if="paymentRequestData.possibleAssets.length">
                    <strong> Positionen auswählen </strong>
                    <table class="table table-striped align-middle mb-3">
                        <thead class="table-light">
                            <tr>
                                <th style="width: 5%;">
                                    Gewählt
                                </th>
                                <th style="width: 55%;">
                                    Typ
                                </th>
                                <th class="text-end" style="width: 40%;">
                                    Betrag
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(request, index) in paymentRequestData.possibleAssets" :key="index">
                                <td>
                                    <input
                                        v-model="selectedAssets"
                                        type="checkbox"
                                        :value="request">
                                </td>
                                <td>{{ request.label }}</td>
                                <td>{{ formatCurrencyCents(request.amount) }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-if="files.length">
                        <strong> Rechnung anhängen </strong>
                        <table class="table table-striped align-middle mb-3">
                            <thead class="table-light">
                                <tr>
                                    <th style="width: 5%;">
                                        Gewählt
                                    </th>
                                    <th style="width: 55%;">
                                        Dateiname
                                    </th>
                                    <th class="text-end" style="width: 40%;">
                                        Typ
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(document, index) in files" :key="index">
                                    <td>
                                        <input
                                            v-model="selectedDocuments"
                                            type="checkbox"
                                            :value="document">
                                    </td>
                                    <td>{{ document.attributes.fileName }}</td>
                                    <td>{{ document.attributes.fileCategoryName }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="d-flex justify-content-end mb-3">
                    <b-button
                        :disabled="hideButton"
                        :variant="hideButton ? 'secondary' : 'primary'"
                        @click="sendPaymentRequest">
                        Zahlungsaufforderung senden
                    </b-button>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
    import {formatDate} from '@js/functions/date';
    import {formatCurrencyCents} from '@js/functions/format';
    import Container from '@services/Container';

    const container = Container.getInstance();
    const recordRepository = container.getDesktopRecordRepository();
    const flashMessenger = container.getFlashMessenger();

    export default {
        name: 'PaymentRequestRecipient',
        props: {
            recordId: {
                type: Number,
                required: true,
            },
            paymentRequestData: {
                type: Object,
                required: true,
            },
            files: {
                type: Array,
                required: true,
            },
            recipient: {
                type: String,
                required: true,
            },
        },
        emits: ['payment-request-sent'],
        data() {
            return {
                isLoading: false,
                selectedAssets: [],
                selectedDocuments: [],
            }
        },
        computed: {
            hideButton() {
                if (this.paymentRequestData.missingInformation.length) {
                    return true;
                }
                if (!this.selectedAssets.length) {
                    return true;
                }
                return false;
            },
            recipientLabel() {
                switch (this.recipient) {
                case 'customer':
                    return 'Kunden'
                case 'lawyer':
                    return 'Anwalt'
                case 'insurance':
                    return 'Versicherung'
                default:
                    return 'Empfänger'
                }
            },
        },
        methods: {
            formatDate,
            formatCurrencyCents,
            sendPaymentRequest() {
                this.isLoading = true;
                recordRepository.sendPaymentRequest(
                    this.recordId,
                    this.recipient,
                    {assets: this.selectedAssets, documents: this.selectedDocuments}
                ).then(
                    (response) => {
                        const message = response.data.attributes.message || 'Zahlungsaufforderung gesendet.';
                        flashMessenger.addSuccess(message);

                        this.selectedAssets = [];

                        this.$emit('payment-request-sent');
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            },

        },
    }
</script>
